import { useIonRouter } from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import RoutePath from '@custom-types/RoutePath';
import {
  getLoginStatus as getLoginStatusFn,
  loginWithPassword as loginWithPasswordFn,
  loginWithAccessCode as loginWithAccessCodeFn,
} from '@pages/Auth/service';
import { get as getCompanyFn } from '@pages/Companies/service';
import { get as getEventFn } from '@pages/Event/service';
import { generateRoute } from '@services/navigation-service';
import { setCompanyInfo } from '@state/company';
import { getEventInfo, setEventInfo } from '@state/event';
import { getKioskEnabled, setKioskEnabled } from '@state/kiosk';
import { loggedIn, loggedOut } from '@state/system';
import { setPeopleId, setUser } from '@state/user';
import { Capacitor } from '~capacitor-plugins/core';
import { SplashScreen } from '~capacitor-plugins/splash-screen';

import useLoading from './useLoading';

type Credential = {
  email?: string;
  password?: string;
  accessCode?: string;
};

export enum LoginType {
  WithPassword,
  WithAccessCode,
  CheckStatus,
}

const selector = createSelector(
  getEventInfo,
  getKioskEnabled,
  ({ id: storeEventId }, isKioskEnabled) => ({
    storeEventId,
    isKioskEnabled,
  })
);

/*
  useLogin is for handling CheckIn app login logic only.
*/
export default function useLogin(type: LoginType, credential?: Credential) {
  const dispatch = useDispatch();
  const { push: pushRoute } = useIonRouter();
  const { storeEventId, isKioskEnabled } = useSelector(selector);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Do not show loader while checking login status, as splash screen loader is already being shown (ie. avoid two consecutive loader screens)
  const showLoader = loading && (!Capacitor.isNativePlatform() || type !== LoginType.CheckStatus);
  useLoading(showLoader);
  const { email, password, accessCode } = credential ?? {};

  const getLandingPage = useCallback(
    (eventId: string) => {
      if (isKioskEnabled) {
        return generateRoute(RoutePath.KioskSetup, { eventId });
      }

      return eventId
        ? generateRoute(RoutePath.EventDetail, { eventId })
        : generateRoute(RoutePath.EventList);
    },
    [isKioskEnabled]
  );

  const [fn, pass] = useMemo(() => {
    if (type === LoginType.WithPassword && email && password) {
      return [loginWithPasswordFn, password];
    }

    if (type === LoginType.WithAccessCode && email && accessCode) {
      return [loginWithAccessCodeFn, accessCode];
    }

    if (type === LoginType.CheckStatus) {
      return [getLoginStatusFn];
    }

    // If we reach here, we don't have the required credentials
    return [];
  }, [type, email, password, accessCode]);

  useEffect(() => {
    const effect = async () => {
      try {
        setLoading(true);
        setError(false);
        const {
          userId: id,
          userRole: role,
          eventId,
          firstName,
          peopleId,
          companyId,
          email: userEmail,
        } = await fn!(email!, pass!);

        if (!id) {
          throw new Error('User is not logged in');
        }

        if (eventId) {
          const eventIdStr = String(eventId);
          const event = await getEventFn(eventIdStr);
          dispatch(
            setEventInfo({
              id: eventIdStr,
              name: event.name,
              imageUrl: event?.eventIcon?.standardSizeUrl,
              startDate: event.startDate,
              endDate: event.endDate,
              location: event.locationName,
            })
          );
          if (companyId) {
            const { name: companyName } = await getCompanyFn(eventIdStr, companyId);
            dispatch(setCompanyInfo({ id: companyId, name: companyName }));
          }
        }
        if (peopleId) {
          dispatch(setPeopleId(peopleId));
        }
        dispatch(loggedIn());
        dispatch(
          setUser({
            userId: id,
            userRole: role,
            firstName,
            peopleId: peopleId ?? '',
            email: userEmail ?? '',
          })
        );
        dispatch(setKioskEnabled(false));
        setLoading(false);

        const route = getLandingPage(eventId ?? storeEventId);
        pushRoute(route, 'root', 'replace');
      } catch {
        if (type === LoginType.CheckStatus) {
          dispatch(loggedOut());
        }

        setError(true);
        setLoading(false);
      }
    };
    if (fn !== undefined) {
      // skipcq: JS-0328
      effect();
    }
  }, [fn, email, pass!]);

  useEffect(() => {
    if (type === LoginType.CheckStatus && !loading) {
      SplashScreen.hide();
    }
  }, [loading, type]);

  return { loading, error };
}
